import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { api, fileUrl, formatApiError } from "../lib/api";
import { BookingCalendar, MonthNav, CalendarLegend } from "../components/BookingCalendar";
import { fmtDate } from "./Booking";
import { toast } from "sonner";
import { LogOut, Users, Calendar as CalIcon, Home as HomeIcon, Key, FileText, History, Gift, Power } from "lucide-react";

const TABS = [
  { id: "reservations", label: "Réservations", icon: <Users size={16}/> },
  { id: "calendar", label: "Calendrier", icon: <CalIcon size={16}/> },
  { id: "huts", label: "Huttes (CMS)", icon: <HomeIcon size={16}/> },
  { id: "codes", label: "Codes d'accès", icon: <Key size={16}/> },
  { id: "free", label: "Réservation offerte", icon: <Gift size={16}/> },
  { id: "content", label: "Contenu & Règlement", icon: <FileText size={16}/> },
  { id: "audit", label: "Journal d'audit", icon: <History size={16}/> },
];

export default function AdminDashboard() {
  const { admin, loading, logout } = useAuth();
  const [tab, setTab] = useState("reservations");
  const [online, setOnline] = useState(true);
  useEffect(() => { api.get("/site-status").then(r => setOnline(r.data.online)).catch(()=>{}); }, []);
  const toggleSite = async () => {
    try {
      const { data } = await api.patch("/admin/site-status", { online: !online });
      setOnline(data.online);
      toast.success(data.online ? "Site publié (en ligne)" : "Site mis hors ligne");
    } catch(e) { toast.error(formatApiError(e)); }
  };
  if (loading) return <div className="p-8 text-stone-500">Chargement…</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;
  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-dashboard">
      <div className="bg-emerald-900 text-white">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="font-serif text-lg">AGCVS · Administration</div>
          <div className="flex items-center gap-4 text-sm">
            <button onClick={toggleSite} data-testid="site-toggle-btn"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-semibold text-xs ${online ? "bg-emerald-700 hover:bg-emerald-600 text-white" : "bg-red-600 hover:bg-red-500 text-white"}`}>
              <Power size={13}/> {online ? "Site en ligne — Mettre hors ligne" : "Site hors ligne — Publier"}
            </button>
            <span className="text-stone-200">{admin.email}</span>
            <Link to="/" className="hover:underline" data-testid="admin-view-site">Voir le site</Link>
            <button onClick={logout} className="inline-flex items-center gap-1 hover:underline" data-testid="admin-logout"><LogOut size={14}/> Déconnexion</button>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-wrap gap-1 mb-6 border-b border-stone-200 pb-1">
          {TABS.map(t => (
            <button key={t.id} onClick={()=>setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-t-md whitespace-nowrap ${tab===t.id ? "bg-white border border-b-white border-stone-200 text-emerald-900 font-semibold" : "text-stone-600 hover:text-emerald-900"}`}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>
        {tab === "reservations" && <Reservations/>}
        {tab === "calendar" && <CalendarMgmt/>}
        {tab === "huts" && <HutsCMS/>}
        {tab === "codes" && <Codes/>}
        {tab === "free" && <FreeBooking/>}
        {tab === "content" && <Content/>}
        {tab === "audit" && <Audit/>}
      </div>
    </div>
  );
}

const STATUS_FR = {
  CONFIRMED: "Confirmée",
  PENDING: "Paiement non finalisé",
  CANCELLED: "Annulée",
  EXPIRED: "Expirée",
  BLOCKED: "Blocage manuel",
};

const GROUPS = [
  { key: "CONFIRMED", title: "Nuits réellement réservées (payées ou offertes)", statuses: ["CONFIRMED"], defaultOpen: true, testId: "group-confirmed" },
  { key: "BLOCKED", title: "Nuits en blocage manuel", statuses: ["BLOCKED"], defaultOpen: false, testId: "group-blocked" },
  { key: "OTHER", title: "Nuits test ou non finalisées", statuses: ["PENDING", "CANCELLED", "EXPIRED"], defaultOpen: false, testId: "group-other" },
];

function Reservations() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState({ CONFIRMED: true, BLOCKED: false, OTHER: false });
  const [expanded, setExpanded] = useState(null);
  const load = () => api.get("/admin/reservations").then(r=>setItems(r.data));
  useEffect(() => { load(); }, []);
  const cancel = async (ref) => {
    if (!window.confirm(`Annuler la réservation ${ref} et rembourser si applicable ?`)) return;
    try { const { data } = await api.post(`/admin/reservations/${ref}/cancel`); toast.success(data.refund_id ? "Annulée + remboursée" : "Annulée"); load(); }
    catch(e){ toast.error(formatApiError(e)); }
  };
  const unblock = async (hutId, d) => {
    try { await api.post("/admin/calendar/set", { hut_id: hutId, date: d, status: "AVAILABLE" }); toast.success("Date débloquée"); load(); }
    catch(e){ toast.error(formatApiError(e)); }
  };
  const resendEmail = async (ref) => {
    try { await api.post(`/admin/reservations/${ref}/resend-email`); toast.success("Email de confirmation renvoyé au client"); load(); }
    catch(e){ toast.error(formatApiError(e)); }
  };
  const fmtDate = (iso) => iso ? new Date(iso + "T12:00:00").toLocaleDateString("fr-FR", { day:"numeric", month:"long", year:"numeric" }) : "";
  return (
    <div className="space-y-4" data-testid="admin-reservations">
      <p className="text-xs text-stone-500">Seules les réservations <strong>« Confirmée »</strong> et les <strong>« Blocage manuel »</strong> bloquent une date du calendrier. Les « Paiement non finalisé » (visiteur ayant abandonné avant de payer), « Expirée » (délai dépassé) et « Annulée » ne bloquent rien.</p>
      {GROUPS.map(g => {
        const rows = items.filter(r => g.statuses.includes(r.status));
        return (
          <div key={g.key} className="bg-white border border-stone-200 rounded-xl overflow-hidden" data-testid={g.testId}>
            <button onClick={()=>setOpen({...open, [g.key]: !open[g.key]})} data-testid={`${g.testId}-toggle`}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-stone-50">
              <span className="font-serif text-lg text-emerald-900">{g.title} <span className="text-sm font-sans text-stone-500">({rows.length})</span></span>
              <span className="text-stone-500 text-sm">{open[g.key] ? "▲ Réduire" : "▼ Dérouler"}</span>
            </button>
            {open[g.key] && (
              rows.length === 0 ? (
                <div className="px-4 pb-4 text-sm text-stone-500">Aucune entrée.</div>
              ) : (
              <div className="overflow-x-auto px-4 pb-4">
                <table className="w-full text-sm">
                  <thead className="text-left text-stone-500 border-b border-stone-200">
                    <tr><th className="py-2">Réf.</th><th>Nuit du</th><th>Hutte</th><th>Client</th><th>Email</th><th>Pers.</th><th>Montant</th><th>Statut</th><th>Action</th></tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <React.Fragment key={r.reference}>
                      <tr className={`border-b border-stone-100 ${!["CONFIRMED","BLOCKED"].includes(r.status) ? "opacity-60" : ""}`} data-testid={`res-row-${r.reference}`}>
                        <td className="py-2 font-mono text-xs">{r.reference}</td>
                        <td>{fmtDate(r.date)}</td>
                        <td className="max-w-[160px] truncate">{r.hut_name}</td>
                        <td>{r.first_name} {r.last_name}</td>
                        <td className="text-xs">{r.email}</td>
                        <td>{r.guests || "—"}</td>
                        <td>{r.status === "BLOCKED" ? "—" : <>{r.amount} € {r.payment_method==="FREE_ADMIN" && "(offerte)"}</>}</td>
                        <td><StatusBadge s={r.status}/></td>
                        <td>
                          {r.status !== "BLOCKED" && <button onClick={()=>setExpanded(expanded===r.reference?null:r.reference)} className="text-emerald-700 hover:underline text-xs mr-3" data-testid={`details-${r.reference}`}>Détails</button>}
                          {r.status === "CONFIRMED" && <button onClick={()=>resendEmail(r.reference)} className="text-emerald-700 hover:underline text-xs mr-3" data-testid={`resend-${r.reference}`}>Renvoyer l'email</button>}
                          {r.status === "CONFIRMED" && <button onClick={()=>cancel(r.reference)} className="text-red-700 hover:underline text-xs" data-testid={`cancel-${r.reference}`}>Annuler & rembourser</button>}
                          {r.status === "BLOCKED" && <button onClick={()=>unblock(r.hut_id, r.date)} className="text-emerald-700 hover:underline text-xs" data-testid={`unblock-${r.date}`}>Débloquer la date</button>}
                        </td>
                      </tr>
                      {expanded === r.reference && (
                        <tr className="bg-stone-50 border-b border-stone-100" data-testid={`details-row-${r.reference}`}>
                          <td colSpan={9} className="p-3 text-xs text-stone-700">
                            <div className="grid gap-1 sm:grid-cols-2">
                              <div><strong>Âge :</strong> {r.age ?? "—"} ans · <strong>Téléphone :</strong> {r.phone || "—"}</div>
                              <div><strong>Adresse :</strong> {r.address || "—"}</div>
                              <div className="sm:col-span-2"><strong>Occupants :</strong>{" "}
                                {(r.occupants && r.occupants.length) ? r.occupants.map((o,i)=>(
                                  <span key={i} className="inline-block mr-3">{o.first_name} {o.last_name}{o.permit_number ? ` — permis n° ${o.permit_number}` : " — permis non renseigné"}</span>
                                )) : "Non renseignés"}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              )
            )}
          </div>
        );
      })}
    </div>
  );
}

function StatusBadge({ s }) {
  const map = { CONFIRMED:"bg-emerald-100 text-emerald-800", PENDING:"bg-amber-100 text-amber-800", CANCELLED:"bg-red-100 text-red-800", EXPIRED:"bg-stone-100 text-stone-600", BLOCKED:"bg-stone-800 text-white" };
  return <span className={`px-2 py-0.5 rounded text-xs ${map[s]||"bg-stone-100"}`}>{STATUS_FR[s] || s}</span>;
}

function CalendarMgmt() {
  const [huts, setHuts] = useState([]);
  const [hutId, setHutId] = useState("");
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());
  const [days, setDays] = useState([]);
  useEffect(() => { api.get("/admin/huts").then(r=>{
    const sorted = [...r.data].sort((a,b) => (a.status === "ACTIVE" ? 0 : 1) - (b.status === "ACTIVE" ? 0 : 1));
    setHuts(sorted);
    if(!hutId && sorted[0]) setHutId(sorted[0].id);
  }); }, []);
  const load = () => {
    if (!hutId) return;
    const s = fmtDate(new Date(year,month,1));
    const e = fmtDate(new Date(year,month+1,0));
    api.get(`/calendar/${hutId}`, { params:{start:s,end:e} }).then(r=>setDays(r.data.days));
  };
  useEffect(() => { load(); }, [hutId, month, year]);
  const toggle = async (iso, cur) => {
    const next = cur === "AVAILABLE" ? "BLOCKED_ADMIN" : "AVAILABLE";
    try { await api.post("/admin/calendar/set", { hut_id: hutId, date: iso, status: next }); load(); }
    catch(e){ toast.error(formatApiError(e)); }
  };
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4" data-testid="admin-calendar">
      <h2 className="font-serif text-xl text-emerald-900 mb-3">Gestion du calendrier</h2>
      <select value={hutId} onChange={e=>setHutId(e.target.value)} className="border rounded-md px-3 py-2 mb-4" data-testid="admin-cal-hut">
        {huts.map(h => <option key={h.id} value={h.id}>{h.name}{h.status !== "ACTIVE" ? " (désactivée)" : ""}</option>)}
      </select>
      {huts.find(h=>h.id===hutId)?.status !== "ACTIVE" && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900" data-testid="cal-hut-disabled-note">
          ⚠️ Cette hutte est désactivée : toutes ses dates apparaissent en rouge « bloquée », c'est normal. Sélectionnez l'autre hutte ci-dessus pour gérer son calendrier.
        </div>
      )}
      <p className="text-xs text-stone-500 mb-3">Cliquez sur une date verte pour la bloquer, sur une date rouge (bloquée) pour la débloquer, ou sur une date jaune (en attente) pour libérer immédiatement la réservation en cours. Les jours « réservés » (paiement confirmé) ne peuvent pas être modifiés ici — passez par l'onglet Réservations.</p>
      <MonthNav month={month} year={year} onChange={(m,y)=>{setMonth(m);setYear(y);}}/>
      <BookingCalendar days={days} month={month} year={year} adminMode onAdminToggle={toggle}/>
      <CalendarLegend/>
    </div>
  );
}

function HutsCMS() {
  const [huts, setHuts] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/huts").then(r=>setHuts(r.data));
  useEffect(() => { load(); }, []);
  const save = async (h) => {
    try {
      const payload = {...h};
      if (typeof payload.equipment === "string") payload.equipment = payload.equipment.split("\n").filter(Boolean);
      if (typeof payload.photos === "string") payload.photos = payload.photos.split("\n").filter(Boolean);
      await api.patch(`/admin/huts/${h.id}`, payload);
      toast.success("Enregistré"); setEditing(null); load();
    } catch(e){ toast.error(formatApiError(e)); }
  };
  const upload = async (h, field, file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers:{"Content-Type":"multipart/form-data"} });
      if (field === "photos") {
        const cur = h.photos || [];
        await api.patch(`/admin/huts/${h.id}`, { photos: [...cur, data.path] });
      } else {
        await api.patch(`/admin/huts/${h.id}`, { [field]: data.path });
      }
      toast.success("Photo ajoutée"); load();
    } catch(e){ toast.error(formatApiError(e)); }
  };
  const removePhoto = async (h, idx) => {
    const photos = [...(h.photos||[])]; photos.splice(idx,1);
    await api.patch(`/admin/huts/${h.id}`, { photos }); load();
  };
  return (
    <div className="space-y-6" data-testid="admin-huts">
      {huts.map(h => (
        <div key={h.id} className="bg-white border border-stone-200 rounded-xl p-4">
          <h3 className="font-serif text-xl text-emerald-900 mb-3">{h.name}</h3>
          {editing === h.id ? (
            <div className="grid gap-3 md:grid-cols-2">
              <F l="Nom" v={h.name} on={v=>setHuts(huts.map(x=>x.id===h.id?{...x,name:v}:x))}/>
              <F l="Statut (ACTIVE / MAINTENANCE_DISABLED)" v={h.status} on={v=>setHuts(huts.map(x=>x.id===h.id?{...x,status:v}:x))}/>
              <F l="Capacité" v={h.max_capacity} type="number" on={v=>setHuts(huts.map(x=>x.id===h.id?{...x,max_capacity:Number(v)}:x))}/>
              <F l="Couchages" v={h.beds} type="number" on={v=>setHuts(huts.map(x=>x.id===h.id?{...x,beds:Number(v)}:x))}/>
              <F l="Prix / nuit (€)" v={h.price_per_night} type="number" on={v=>setHuts(huts.map(x=>x.id===h.id?{...x,price_per_night:Number(v)}:x))}/>
              <F l="Chauffage" v={h.heating||""} on={v=>setHuts(huts.map(x=>x.id===h.id?{...x,heating:v}:x))}/>
              <F l="URL parking (Google Maps)" v={h.parking_map_url||""} on={v=>setHuts(huts.map(x=>x.id===h.id?{...x,parking_map_url:v}:x))}/>
              <F l="URL hutte (Google Maps)" v={h.hut_map_url||""} on={v=>setHuts(huts.map(x=>x.id===h.id?{...x,hut_map_url:v}:x))}/>
              <div className="md:col-span-2"><FT l="Description" v={h.description||""} on={v=>setHuts(huts.map(x=>x.id===h.id?{...x,description:v}:x))}/></div>
              <div className="md:col-span-2"><FT l="Équipements (un par ligne)" v={Array.isArray(h.equipment)?h.equipment.join("\n"):h.equipment||""} on={v=>setHuts(huts.map(x=>x.id===h.id?{...x,equipment:v}:x))}/></div>
              <div className="md:col-span-2"><FT l="Informations pratiques" v={h.practical_info||""} on={v=>setHuts(huts.map(x=>x.id===h.id?{...x,practical_info:v}:x))}/></div>
              <div className="md:col-span-2"><FT l="Résumé des règles" v={h.rules_summary||""} on={v=>setHuts(huts.map(x=>x.id===h.id?{...x,rules_summary:v}:x))}/></div>
              <div className="md:col-span-2 flex gap-2">
                <button onClick={()=>save(h)} className="bg-emerald-800 text-white px-4 py-2 rounded-md" data-testid={`save-hut-${h.slug}`}>Enregistrer</button>
                <button onClick={()=>{setEditing(null); load();}} className="border px-4 py-2 rounded-md">Annuler</button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-stone-700">
              <p>Statut : {h.status} · Prix : {h.price_per_night} € · Capacité : {h.max_capacity}</p>
              <button onClick={()=>setEditing(h.id)} className="mt-2 text-emerald-700 hover:underline" data-testid={`edit-hut-${h.slug}`}>Modifier les informations</button>
            </div>
          )}
          <div className="mt-4">
            <div className="text-sm font-semibold text-stone-700 mb-2">Photos ({(h.photos||[]).length})</div>
            <div className="flex flex-wrap gap-2 mb-2">
              {(h.photos||[]).map((p,i)=>(
                <div key={i} className="relative w-24 h-24">
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover rounded border"/>
                  <button onClick={()=>removePhoto(h,i)} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 text-xs">×</button>
                </div>
              ))}
            </div>
            <input type="file" accept="image/*" onChange={e=>e.target.files[0] && upload(h,"photos",e.target.files[0])} data-testid={`upload-photo-${h.slug}`}/>
            <div className="mt-2 grid gap-2 sm:grid-cols-2 text-sm">
              <div>Vue aérienne : <input type="file" accept="image/*" onChange={e=>e.target.files[0] && upload(h,"aerial_photo_url",e.target.files[0])}/></div>
              <div>Cadastre : <input type="file" accept="image/*" onChange={e=>e.target.files[0] && upload(h,"cadastral_photo_url",e.target.files[0])}/></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function F({l,v,on,type="text"}){ return <div><label className="text-xs text-stone-500 block">{l}</label><input type={type} value={v} onChange={e=>on(e.target.value)} className="w-full border rounded-md px-2 py-1.5 text-sm"/></div>; }
function FT({l,v,on}){ return <div><label className="text-xs text-stone-500 block">{l}</label><textarea rows={4} value={v} onChange={e=>on(e.target.value)} className="w-full border rounded-md px-2 py-1.5 text-sm"/></div>; }

function Codes() {
  const [huts, setHuts] = useState([]);
  const load = () => api.get("/admin/huts").then(r=>setHuts(r.data));
  useEffect(() => { load(); }, []);
  const save = async (h, patch) => {
    try { await api.patch(`/admin/huts/${h.id}/codes`, patch); toast.success("Codes mis à jour"); load(); }
    catch(e){ toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="admin-codes">
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900">
        ⚠️ Ces codes sont sensibles. Ils ne sont jamais affichés publiquement. Ils sont uniquement envoyés dans l'email de confirmation au client concerné.
      </div>
      {huts.map(h => (
        <CodeRow key={h.id} h={h} onSave={save}/>
      ))}
    </div>
  );
}
function CodeRow({h, onSave}) {
  const [lb, setLb] = useState(h.lockbox_code||"");
  const [bl, setBl] = useState(h.boat_lock_code||"");
  const [ia, setIa] = useState(h.internal_access_info||"");
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4">
      <h3 className="font-serif text-lg text-emerald-900 mb-3">{h.name}</h3>
      <div className="grid gap-3 md:grid-cols-2">
        <F l="Code boîte à clé" v={lb} on={setLb}/>
        <F l="Code cadenas barque" v={bl} on={setBl}/>
        <div className="md:col-span-2"><FT l="Instructions d'accès internes" v={ia} on={setIa}/></div>
      </div>
      <button onClick={()=>onSave(h,{lockbox_code:lb, boat_lock_code:bl, internal_access_info:ia})} className="mt-3 bg-emerald-800 text-white px-4 py-2 rounded-md" data-testid={`save-codes-${h.slug}`}>Enregistrer les codes</button>
    </div>
  );
}

function FreeBooking() {
  const [huts, setHuts] = useState([]);
  const [f, setF] = useState({ hut_id:"", date:"", first_name:"", last_name:"", email:"", phone:"", guests:1, note:"" });
  useEffect(()=>{ api.get("/admin/huts").then(r=>{ setHuts(r.data); if(r.data[0]) setF(s=>({...s,hut_id:r.data[0].id})); }); },[]);
  const submit = async () => {
    try { const { data } = await api.post("/admin/reservations/free", f); toast.success(`Réservation créée : ${data.reference}`); setF({...f, first_name:"", last_name:"", email:"", phone:"", date:""}); }
    catch(e){ toast.error(formatApiError(e)); }
  };
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4 max-w-2xl" data-testid="admin-free">
      <h2 className="font-serif text-xl text-emerald-900 mb-3">Créer une réservation offerte (membre)</h2>
      <div className="grid gap-3 md:grid-cols-2">
        <div><label className="text-xs text-stone-500 block">Hutte</label>
          <select value={f.hut_id} onChange={e=>setF({...f,hut_id:e.target.value})} className="w-full border rounded-md px-2 py-1.5 text-sm">{huts.map(h=><option key={h.id} value={h.id}>{h.name}</option>)}</select>
        </div>
        <F l="Date (AAAA-MM-JJ)" v={f.date} on={v=>setF({...f,date:v})}/>
        <F l="Prénom" v={f.first_name} on={v=>setF({...f,first_name:v})}/>
        <F l="Nom" v={f.last_name} on={v=>setF({...f,last_name:v})}/>
        <F l="Email" v={f.email} on={v=>setF({...f,email:v})}/>
        <F l="Téléphone" v={f.phone} on={v=>setF({...f,phone:v})}/>
        <F l="Personnes" type="number" v={f.guests} on={v=>setF({...f,guests:Number(v)})}/>
        <div className="md:col-span-2"><FT l="Note interne" v={f.note} on={v=>setF({...f,note:v})}/></div>
      </div>
      <button onClick={submit} className="mt-3 bg-amber-600 text-white px-4 py-2 rounded-md" data-testid="free-submit">Créer la réservation offerte</button>
    </div>
  );
}

function Content() {
  const [c, setC] = useState({});
  const [reg, setReg] = useState({});
  useEffect(()=>{ api.get("/content").then(r=>{ setC(r.data.content||{}); setReg(r.data.regulations||{}); }); },[]);
  const save = async () => {
    try { await api.patch("/admin/content", c); toast.success("Contenu mis à jour"); }
    catch(e){ toast.error(formatApiError(e)); }
  };
  const uploadReg = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd);
    await api.patch("/admin/regulations", { pdf_url: data.url });
    toast.success("Règlement mis à jour");
    api.get("/content").then(r=>setReg(r.data.regulations||{}));
  };
  return (
    <div className="space-y-4" data-testid="admin-content">
      <div className="bg-white border rounded-xl p-4">
        <h2 className="font-serif text-xl text-emerald-900 mb-3">Contenu du site</h2>
        <div className="grid gap-3 md:grid-cols-2">
          <F l="Nom" v={c.association_name||""} on={v=>setC({...c,association_name:v})}/>
          <F l="Nom complet" v={c.association_full_name||""} on={v=>setC({...c,association_full_name:v})}/>
          <F l="Adresse" v={c.address||""} on={v=>setC({...c,address:v})}/>
          <F l="Email de contact" v={c.contact_email||""} on={v=>setC({...c,contact_email:v})}/>
          <F l="Groupe Facebook (URL)" v={c.facebook_url||""} on={v=>setC({...c,facebook_url:v})}/>
          <div className="md:col-span-2"><FT l="Texte de présentation" v={c.presentation||""} on={v=>setC({...c,presentation:v})}/></div>
        </div>
        <button onClick={save} className="mt-3 bg-emerald-800 text-white px-4 py-2 rounded-md" data-testid="save-content">Enregistrer</button>
      </div>
      <div className="bg-white border rounded-xl p-4">
        <h2 className="font-serif text-xl text-emerald-900 mb-3">Règlement intérieur (PDF)</h2>
        {reg.pdf_url && <p className="text-sm mb-2">Actuel : <a href={reg.pdf_url} target="_blank" rel="noreferrer" className="text-emerald-700 underline">version {reg.version}</a></p>}
        <input type="file" accept="application/pdf" onChange={e=>e.target.files[0] && uploadReg(e.target.files[0])} data-testid="reg-upload"/>
      </div>
    </div>
  );
}

function Audit() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/audit").then(r=>setItems(r.data)); }, []);
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4" data-testid="admin-audit">
      <h2 className="font-serif text-xl text-emerald-900 mb-3">Journal d'audit ({items.length})</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-stone-500 border-b">
            <tr><th className="py-2">Date</th><th>Action</th><th>Cible</th><th>Hutte</th><th>Date affectée</th><th>Ancien</th><th>Nouveau</th><th>Détails</th></tr>
          </thead>
          <tbody>
            {items.map(a => (
              <tr key={a.id} className="border-b border-stone-100">
                <td className="py-2 text-xs">{new Date(a.timestamp).toLocaleString("fr-FR")}</td>
                <td className="text-xs font-mono">{a.action}</td>
                <td className="text-xs">{a.target}</td>
                <td className="text-xs">{a.hut_id?.slice(0,8)}</td>
                <td className="text-xs">{a.affected_date}</td>
                <td className="text-xs">{a.old_value}</td>
                <td className="text-xs">{a.new_value}</td>
                <td className="text-xs">{a.details}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
